import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, catchError, finalize } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CalendarView,
  CrewGroup,
  CrewGroupButtonAction,
  LegendComponent,
  ProductionCalendarComponent,
  ProductionEvent,
  ProductionEventAction,
  ScheduleButtonType,
} from '@groundworks/ngx-groundworks-ui';
import { getUserRoles } from '../../models/roles.model';
import { AvailabilityService } from '../../services/availability.service';
import { BranchService } from '../../services/branch.service';
import { CalendarService } from '../../services/calendar.service';
import { CrewScheduleStore } from '../../services/crew-schedule-store.service';
import { InstallDateManagementService } from '../../services/install-date-management.service';
import { ModalService } from '../../services/modal.service';
import { ForemanOption, ForemanRole, InstallDateFormData } from '../../models/install-event.types';
import { ScheduleProjectPanelComponent } from '../schedule-project-panel/schedule-project-panel.component';
import { SideModalComponent } from '../side-modal/side-modal.component';
import { WeeklyCalendarMetricsComponent } from './weekly-calendar-metrics/weekly-calendar-metrics.component';
import { toDateString } from '../../utils/schedule-calculation';

function departmentToRole(department: string | undefined): ForemanRole {
  switch (department) {
    case 'Sub-Contractor': return 'sub-contractor';
    case 'Co-Foreman': return 'co-foreman';
    case 'Field Superintendent': return 'field-superintendent';
    default: return 'foreman';
  }
}

@Component({
  selector: 'app-week-view-calendar',
  imports: [
    SideModalComponent,
    ProductionCalendarComponent,
    LegendComponent,
    WeeklyCalendarMetricsComponent,
    ScheduleProjectPanelComponent,
  ],
  templateUrl: './week-view-calendar.component.html',
  styles: [`
    :host ::ng-deep .group-header-content > span:not(.item-list):has(> span.fw-bold) {
      color: #6C757D;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-flex flex-column flex-grow-1 overflow-hidden' },
})
export class WeekViewCalendarComponent {
  private readonly store = inject(CrewScheduleStore);

  readonly crewGroups = this.store.crewGroups;
  readonly displayCrewGroups = computed<CrewGroup[] | null>(() => {
    const groups = this.crewGroups();
    if (!groups || !this.schedulingMode()) return groups;
    const foremanIds = new Set(
      this.panelForemanOptions().filter(o => o.role === 'foreman').map(o => o.id)
    );
    return groups.map(cg => {
      if (!foremanIds.has(cg.id)) return cg;
      let changed = false;
      const schedule = cg.schedule.map(day => {
        if (day.buttonType === ScheduleButtonType.ADD && !day.buttonDisabled) {
          changed = true;
          return { ...day, buttonDisabled: true };
        }
        return day;
      });
      return changed ? { ...cg, schedule } : cg;
    });
  });
  readonly isLoading = this.store.isLoading;
  readonly isError = this.store.isError;
  readonly scrollToCrewGroupId = this.store.scrollToCrewGroupId;
  readonly legendItems = this.store.legendItems;
  readonly warnings = this.store.warnings;
  private readonly availabilityService = inject(AvailabilityService);
  private readonly installDateService = inject(InstallDateManagementService);
  private readonly calendarService = inject(CalendarService);
  private readonly branchService = inject(BranchService);
  private readonly modalService = inject(ModalService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(SideModalComponent) private readonly sideModal!: SideModalComponent;

  private foremanRolesLoading = false;
  initialDate: Date | null = null;
  readonly today = new Date();
  readonly sectionOrder: string[] = ['Sub-Contractor', 'Co-Foreman', 'Field Superintendent'];
  private opportunityId: string | null = null;
  private projectId: string | null = null;
  customerSatisfactionVisit = false;
  readonly selectedBranch = toSignal(this.branchService.getSelectedBranch(), { initialValue: null });
  readonly startDate = this.store.startDate;
  readonly schedulingMode = signal(false);
  readonly panelOpportunityId = signal<string>('');
  readonly panelProjectId = signal<string>('');
  readonly panelBranchId = signal<string | undefined>(undefined);
  readonly panelInstallDates = signal<InstallDateFormData[]>([]);
  // TODO(MOD-3420 option a): fold role into the store's CalendarCrewScheduleInfo so
  // the override signal can go away. Blocked on a backend field and a data-owner
  // decision about who authoritatively owns a crew member's role.
  readonly panelForemanOptionsOverride = signal<ForemanOption[] | null>(null);
  readonly panelForemanOptions = computed<ForemanOption[]>(() =>
    this.panelForemanOptionsOverride() ??
      (this.store.allCrewGroups() ?? []).map(cg => ({ id: cg.id, name: cg.name, role: 'foreman' }))
  );

  get canEdit(): boolean {
    const roles = getUserRoles();
    return roles.SuperUser || roles.GeneralManager || roles.OfficeManager || roles.ProductionManager || roles.ProductionAdmin || roles.FieldSuperintendent || roles.RegionalManager || roles.FieldSupport;
  }

  constructor() {
    this.store.initialize(this.destroyRef);

    // Any path out of scheduling mode (URL change, panel exit, future exits) must
    // drop the role-enriched override so the next entry starts from a clean slate.
    effect(() => {
      if (!this.schedulingMode()) {
        this.panelForemanOptionsOverride.set(null);
      }
    });

    // Fetch role-enriched foreman options whenever scheduling mode is active
    // without an override already populated — covers the URL query-param entry
    // path where onEventClicked didn't supply the data.
    effect(() => {
      if (!this.schedulingMode()) return;
      if (this.panelForemanOptionsOverride()) return;
      if (this.foremanRolesLoading) return;
      const branchId = this.selectedBranch()?.id;
      const startDate = this.store.startDate();
      const endDate = this.store.endDate();
      const crewGroups = this.store.allCrewGroups();
      if (!branchId || !startDate || !endDate || !crewGroups?.length) return;
      this.loadForemanRoles(branchId, startDate, endDate);
    });

    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      const branchId = params.get('branchId');
      const foremanId = params.get('foremanId');
      const startDateParam = params.get('startDate');
      this.opportunityId = params.get('opportunityId');
      this.projectId = params.get('projectId');
      this.customerSatisfactionVisit = params.get('customerSatisfactionVisit') === 'true';
      this.schedulingMode.set(!!this.projectId);
      this.panelOpportunityId.set(this.opportunityId ?? '');
      this.panelProjectId.set(this.projectId ?? '');
      this.panelBranchId.set(branchId ?? undefined);

      if (branchId) {
        if (startDateParam) {
          this.store.setIsLoading(true);
        }
        // Only re-hydrate BranchService when its current selection differs from
        // the query-param branch — same-branch navigation (e.g. monthly-capacity
        // → calendar) preserves the full BranchInfo already loaded. The deep-link
        // / refresh path falls through and setSelectedBranchById fills in the
        // full branch info via the cached lookup.
        const currentBranch = this.branchService.getCurrentSelectedBranch();
        if (currentBranch?.id !== branchId) {
          this.branchService.setSelectedBranchById(branchId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        }
      }
      if (foremanId) {
        this.store.setScrollToCrewGroupId(foremanId);
      }
      if (startDateParam) {
        this.initialDate = new Date(startDateParam + 'T00:00:00');
      }
    });
  }

  onWeekChanged(dates: { date: Date }[]): void {
    this.store.setWeekDates(dates);
  }

  onSettings(): void {
    this.sideModal.openSettings();
  }

  onNameClicked(crewGroup: CrewGroup): void {
    this.modalService.openForemanDetailModal(crewGroup.id, this.schedulingMode());
  }

  onEventClicked(event: ProductionEvent): void {
    const eventKey = event.id;
    const opportunityId = this.store.getOpportunityId(eventKey);
    const projectId = this.store.getProjectId(eventKey);
    const branchId = this.selectedBranch()?.id;
    const startDate = this.store.startDate();
    const endDate = this.store.endDate();
    if (!opportunityId || !projectId || !branchId || !startDate || !endDate) {
      this.modalService.openErrorModal(
        'Unable to Open Event',
        'The calendar data may be stale. Please refresh the page and try again.');
      return;
    }

    const crewGroupMembers = this.store.allCrewGroups() ?? [];

    this.calendarService
      .getForemen(branchId, startDate, endDate)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((err) => {
          if (err instanceof HttpErrorResponse) {
            console.error('Failed to load foremen for role enrichment:', err);
            this.modalService.openErrorModal(
              'Unable to Load Foreman Details',
              'Foreman role information could not be loaded. Please try again.');
          } else {
            console.error('Unexpected error loading foremen:', err);
            this.modalService.openErrorModal(
              'Unexpected Error',
              'An unexpected error occurred. Please try again.');
          }
          return EMPTY;
        }),
      )
      .subscribe((foremen) => {
        const departmentById = new Map(foremen.map((f) => [f.id ?? '', f.department]));

        const foremanOptions: ForemanOption[] = crewGroupMembers.map((cg) => ({
          id: cg.id,
          name: cg.name,
          role: departmentToRole(departmentById.get(cg.id)),
        }));

        this.openSchedulingPanelForEvent({
          opportunityId,
          projectId,
          branchId,
          foremanOptions,
          foremanUserId: this.store.getForemanId(eventKey),
        });
      });
  }

  private loadForemanRoles(branchId: string, startDate: Date, endDate: Date): void {
    this.foremanRolesLoading = true;
    this.calendarService
      .getForemen(branchId, startDate, endDate)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((err: unknown) => {
          console.error('Failed to load foremen for role enrichment:', err);
          return EMPTY;
        }),
        finalize(() => { this.foremanRolesLoading = false; }),
      )
      .subscribe((foremen) => {
        const crewGroups = this.store.allCrewGroups() ?? [];
        const departmentById = new Map(foremen.map((f) => [f.id ?? '', f.department]));
        const foremanOptions: ForemanOption[] = crewGroups.map((cg) => ({
          id: cg.id,
          name: cg.name,
          role: departmentToRole(departmentById.get(cg.id)),
        }));
        this.panelForemanOptionsOverride.set(foremanOptions);
      });
  }

  private openSchedulingPanelForEvent(params: {
    opportunityId: string;
    projectId: string;
    branchId: string;
    foremanOptions: ForemanOption[];
    foremanUserId: string | undefined;
  }): void {
    this.opportunityId = params.opportunityId;
    this.projectId = params.projectId;
    this.panelOpportunityId.set(params.opportunityId);
    this.panelProjectId.set(params.projectId);
    this.panelBranchId.set(params.branchId);
    this.panelForemanOptionsOverride.set(params.foremanOptions);
    this.panelInstallDates.set([]);
    this.schedulingMode.set(true);
    if (params.foremanUserId) {
      this.store.setScrollToCrewGroupId(params.foremanUserId);
    }
  }

  onAddClicked(event: CrewGroupButtonAction): void {
    const branchId = this.selectedBranch()?.id;
    if (!branchId) {
      this.modalService.openErrorModal(
        'Unable to Add Availability',
        'No branch is selected. Please select a branch and try again.');
      return;
    }
    this.availabilityService.addAvailability(event.crewGroup.id, event.date, branchId, this.destroyRef);
  }

  onRemoveClicked(event: CrewGroupButtonAction): void {
    const branchId = this.selectedBranch()?.id;
    if (!branchId) {
      this.modalService.openErrorModal(
        'Unable to Remove Availability',
        'No branch is selected. Please select a branch and try again.');
      return;
    }
    this.availabilityService.removeAvailability(event.crewGroup.id, event.date, branchId, this.destroyRef);
  }

  onEventActionClicked(action: ProductionEventAction): void {
    this.installDateService.handleEventAction(action, this.destroyRef);
  }

  onPanelSaved(): void {
    this.store.loadCrewSchedules();
  }

  onPanelExitEditMode(): void {
    this.schedulingMode.set(false);
    this.panelInstallDates.set([]);
  }

  onPanelGoBackToJob(): void {
    const opportunityId = this.opportunityId;
    this.router.navigate(['/viewer'], opportunityId ? { queryParams: { opportunityId } } : undefined);
  }

  onPanelDateRemoved(event: { date: string; foremanUserId: string }): void {
    this.panelInstallDates.update(current =>
      current.filter(d => !(d.date === event.date && d.foremanUserId === event.foremanUserId))
    );
  }

  onScheduleProject(event: CrewGroupButtonAction): void {
    this.appendSchedulingDate(event);
  }

  private appendSchedulingDate(event: CrewGroupButtonAction): void {
    const dateStr = toDateString(event.date);
    const foremanUserId = event.crewGroup.id;
    const targetProjectId = this.panelProjectId();
    const current = this.panelInstallDates();
    const exists = current.some(d =>
      d.date === dateStr
        && d.foremanUserId === foremanUserId
        && (d.targetProjectId ?? '') === targetProjectId,
    );

    if (exists) return;

    this.schedulingMode.set(true);

    this.panelInstallDates.set([
      ...current,
      {
        installDateId: '',
        date: dateStr,
        startTime: '',
        endTime: '',
        foremanUserId,
        isNew: true,
        targetProjectId,
      },
    ]);
  }

  onPanelProjectChanged(projectId: string): void {
    this.panelProjectId.set(projectId);
  }

  private buildPassthroughParams(): Record<string, string> {
    const params: Record<string, string> = {};
    if (this.selectedBranch()?.id) {
      params['branchId'] = this.selectedBranch()!.id!;
    }
    if (this.schedulingMode()) {
      if (this.opportunityId) params['opportunityId'] = this.opportunityId;
      if (this.projectId) params['projectId'] = this.projectId;
    }
    return params;
  }

  private navigateToMonthly(queryParams: Record<string, string>): void {
    this.router.navigate(['/monthly-capacity'], { queryParams });
  }

  onCalendarViewChanged(calendarView: CalendarView) {
    if (calendarView === 'capacity') {
      this.navigateToMonthly(this.buildPassthroughParams());
     }
  }
}
